import { getTransactionDecoder, getTransactionEncoder } from "@solana/kit";
import { VersionedTransaction } from "@solana/web3.js";
import { normalizeVersionedTransactionWireBytes } from "../../shared/solanaTransactionWire.js";
import {
  getWalletSigningAddress,
  getWalletSigningMode,
  isEmbeddedPresignSubmitMode
} from "./walletSigningReadiness.js";

export function base64ToBytes(serializedTransactionBase64) {
  return Uint8Array.from(atob(serializedTransactionBase64), (char) => char.charCodeAt(0));
}

export function bytesToBase64(bytes) {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function resolveWalletAddress(wallet) {
  const walletAddress = getWalletSigningAddress(wallet);
  if (!walletAddress) {
    throw new Error("Wallet is not connected.");
  }
  return walletAddress;
}

function resolveSignTransaction(wallet) {
  const signTransaction = wallet?.signTransaction?.bind(wallet);
  if (typeof signTransaction !== "function") {
    throw new Error("Connected wallet does not support transaction signing.");
  }
  return signTransaction;
}

export function assertWalletCanSignTransaction(wallet) {
  assertWalletCanSubmitTransaction(wallet);
}

export function assertWalletCanSubmitTransaction(wallet) {
  resolveWalletAddress(wallet);
  if (isEmbeddedPresignSubmitMode(getWalletSigningMode(wallet))) {
    if (typeof wallet?.signAndSendTransaction !== "function") {
      throw new Error("Connected wallet does not support transaction submission.");
    }
    return;
  }
  resolveSignTransaction(wallet);
}

function usesFrameworkKitWalletStandardSession(wallet) {
  return wallet?.connector?.kind === "wallet-standard" || String(wallet?.connector?.id || "").startsWith("wallet-standard:");
}

export function deserializeWeb3TransactionBase64(serializedTransactionBase64) {
  return VersionedTransaction.deserialize(base64ToBytes(serializedTransactionBase64));
}

export function serializeWeb3TransactionBase64(transaction) {
  return bytesToBase64(normalizeVersionedTransactionWireBytes(transaction.serialize()));
}

export async function signSerializedTransactionBase64({ wallet, unsignedTransactionBase64, signOptions }) {
  const walletAddress = resolveWalletAddress(wallet);
  const signTransaction = resolveSignTransaction(wallet);
  const unsignedBytes = base64ToBytes(unsignedTransactionBase64);

  if (usesFrameworkKitWalletStandardSession(wallet)) {
    const transaction = getTransactionDecoder().decode(unsignedBytes);
    const signedTransaction = await signTransaction(transaction);
    return {
      walletAddress,
      serializedTransactionBase64: bytesToBase64(
        normalizeVersionedTransactionWireBytes(getTransactionEncoder().encode(signedTransaction))
      )
    };
  }

  const transaction = VersionedTransaction.deserialize(unsignedBytes);
  const signedTransaction = signOptions === undefined
    ? await signTransaction(transaction)
    : await signTransaction(transaction, signOptions);
  return {
    walletAddress,
    serializedTransactionBase64: serializeWeb3TransactionBase64(signedTransaction)
  };
}
